import { useState, useEffect, useRef, RefObject } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { suggestVideos } from '../../services/videoApi';
import { useSuggestions } from '../../hooks/useSuggestions';
import { UseLayoutResult, VideoSuggestion } from './types';
import { useIsMobile } from '../../config/breakpoints';

export const useLayout = (): UseLayoutResult => {
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const isMobile = useIsMobile();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // サジェスト機能を共通フックで管理
  const suggestionHook = useSuggestions({
    apiFunction: suggestVideos,
    testIdPrefix: 'layout-suggestion',
    onSuggestionClick: (suggestion) => {
      const value = suggestion.value || suggestion.title || '';
      setSearchQuery(value);
      navigate(`/?q=${encodeURIComponent(value)}`);
      suggestionHook.setIsInputFocused(false);
    }
  });

  // VideoDetailページかどうかを判定する関数
  const isVideoDetailPage = (): boolean => {
    return !!location.pathname.match(/^\/videos\/[^/]+$/);
  };


  // ヘッダーを表示しないページかどうかを判定
  const shouldHideHeader = (): boolean => {
    // PC版では動画詳細画面と動画編集画面でもヘッダーを表示
    if (!isMobile) {
      return false; // PC版ではヘッダーを常に表示
    }
    // モバイル版では動画詳細ページでヘッダーを非表示
    return isVideoDetailPage();
  };

  // 画面遷移時にスクロール位置をリセット
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // レスポンシブ対応は useIsMobile フックで管理されるため削除

  // URL検索クエリがあれば反映
  useEffect(() => {
    const q = searchParams.get('q') || '';
    setSearchQuery(q);
  }, [searchParams]);

  // 検索クエリが変更されたときにサジェストフックを更新
  useEffect(() => {
    suggestionHook.updateQuery(searchQuery);
  }, [searchQuery, suggestionHook]);

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // 検索クエリが空でも検索を実行
    navigate(`/?q=${encodeURIComponent(searchQuery.trim())}`);
    suggestionHook.setIsInputFocused(false);
  };

  const toggleSearch = () => {
    // 検索クエリが空でない場合は、検索フォームを閉じない
    if (searchQuery.trim() && searchOpen) {
      // 検索入力欄にフォーカスを移す
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
      return; // 検索クエリがあって開いている場合は、フォーカスだけ当てて終了
    }
    
    // 検索フォームを閉じる場合は検索クエリをクリア
    if (searchOpen) {
      setSearchQuery('');
    }
    
    // 検索枠の開閉を切り替え
    setSearchOpen(!searchOpen);
    
    // 検索フォームが開かれたら、検索入力欄にフォーカスする
    if (!searchOpen && searchInputRef.current) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  };

  // 検索フォーム以外の場所がクリックされたら検索フォームを閉じる
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      // 検索クエリがない場合のみ、外側クリックで閉じる
      if (searchOpen && searchRef.current && !searchRef.current.contains(e.target as Node) && !searchQuery.trim()) {
        setSearchOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [searchOpen, searchQuery]);

  return {
    searchQuery,
    setSearchQuery,
    isMobile,
    searchOpen,
    setSearchOpen,
    isInputFocused: suggestionHook.isInputFocused,
    setIsInputFocused: suggestionHook.setIsInputFocused,
    suggestions: suggestionHook.suggestions,
    searchRef,
    searchInputRef,
    selectedSuggestionIndex: suggestionHook.selectedSuggestionIndex,
    handleLogoClick,
    handleSearchSubmit,
    handleSuggestionClick: suggestionHook.handleSuggestionClick,
    handleFocus: suggestionHook.handleFocus,
    handleBlur: suggestionHook.handleBlur,
    handleKeyDown: suggestionHook.handleKeyDown,
    handleSuggestionKeyDown: suggestionHook.handleSuggestionKeyDown,
    toggleSearch,
    shouldHideHeader,
    isVideoDetailPage
  };
}; 
